/**
 * voyado-autocomplete.js
 * Custom element <voyado-autocomplete> that replaces Prestige's predictive search
 * with Voyado Elevate autocomplete results.
 *
 * Imports all API utilities from voyado-elevate.js — no direct Voyado endpoint calls here.
 * All config — API params, panel order, and per-panel settings — is read from
 * #voyado-autocomplete-config (rendered by sections/voyado-autocomplete.liquid).
 * Static sections do not support Shopify blocks, so panel order and visibility are
 * controlled via the "Panel order" text setting in the Customizer.
 */

import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  buildUrl,
  syncCustomerKey,
  waitForConsent,
  hasVoyadoConsent,
  isBotOrCrawler,
  sendClickNotification,
  sendRemoveRecentSearchesNotification,
  sendRemoveRecentlyViewedNotification,
  escapeHtml,
  escapeAttr,
  safeUrl,
  formatPrice,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();

/**
 * Reads all autocomplete configuration from #voyado-autocomplete-config and returns
 * both the API-level params and ordered panel descriptor arrays for zero-state and active-state.
 *
 * Static sections in theme.liquid do not support Shopify blocks, so panel order and
 * per-panel settings are encoded as flat data attributes on the config element.
 * @returns {{ api: Object, zeroPanels: Array<Object>, activePanels: Array<Object> }}
 */
function readAutocompleteConfig() {
  const el = document.getElementById('voyado-autocomplete-config');
  const d = el ? el.dataset : {};

  const api = {
    channels: d.channels || '',
    priceId: d.priceId || '',
    stores: d.stores || '',
    templateId: d.templateId || '',
    viewId: d.viewId || '',
    presentCustom: d.presentCustom || '',
    presentPrices: d.presentPrices || '',
    productRules: d.productRules || '',
    debounceMs: parseInt(d.debounceMs ?? '200', 10) || 200,
  };

  function buildPanel(type) {
    switch (type) {
      case 'phrases':
        return {
          type,
          label: d.phrasesLabel || 'Suchvorschläge',
          labelEmpty: d.phrasesLabelEmpty || 'Trendsuchen',
        };
      case 'products':
        return {
          type,
          label: d.productsLabel || 'Produkte',
          showImage: d.productsShowImage !== 'false',
          showPrice: d.productsShowPrice !== 'false',
          showBrand: d.productsShowBrand !== 'false',
          viewAllLabel: d.productsViewAllLabel || 'Alle __count__ Produkte anzeigen',
        };
      case 'recent_searches':
        return {
          type,
          label: d.recentSearchesLabel || 'Letzte Suchen',
          showRemove: d.recentSearchesShowRemove !== 'false',
        };
      case 'recently_viewed':
        return {
          type,
          label: d.recentlyViewedLabel || 'Zuletzt angesehen',
          showImage: d.recentlyViewedShowImage !== 'false',
          showPrice: d.recentlyViewedShowPrice !== 'false',
          showBrand: d.recentlyViewedShowBrand !== 'false',
          showRemove: d.recentlyViewedShowRemove !== 'false',
        };
      case 'content':
        return {
          type,
          label: d.contentLabel || 'Inhalte',
          listId: d.contentListId || 'content',
          showImage: d.contentShowImage === 'true',
          showDescription: d.contentShowDescription === 'true',
        };
      case 'redirect':
        return { type };
      default:
        return null;
    }
  }

  function parseOrder(str) {
    return str
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(buildPanel)
      .filter(Boolean);
  }

  const zeroPanels = parseOrder(d.zeroStatePanelOrder || 'recent_searches,recently_viewed,phrases');
  const activePanels = parseOrder(d.activeStatePanelOrder || 'phrases,content,redirect');

  if (d.zeroStateShowProducts !== 'false') {
    zeroPanels.push(buildPanel('products'));
  }

  if (d.activeStateShowProducts !== 'false') {
    activePanels.push(buildPanel('products'));
  }

  const labels = {
    noResults: d.labelNoResults || 'Keine Ergebnisse gefunden',
    soldOut: d.labelSoldOut || 'Ausverkauft',
  };

  return { api, zeroPanels, activePanels, labels };
}

/**
 * Sanitises the Voyado highlight string: allows only <mark> tags.
 * Everything else is entity-encoded to prevent XSS.
 * @param {string} html
 * @returns {string}
 */
function sanitiseHighlight(html) {
  if (typeof html !== 'string') return '';
  return html
    .replace(/[&<>"]/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
    }[char]))
    .replace(/\{([^}]+)\}/g, '<mark>$1</mark>');
}

function getProductKey(product) {
  const rawProductKey = product?.key ?? product?.productKey ?? product?.id ?? '';
  return String(rawProductKey || '').trim();
}

function isFavoritedProductKey(productKey) {
  if (!productKey) return false;

  try {
    const value = localStorage.getItem('voyado_favorites');
    const items = value ? JSON.parse(value) : [];
    return Array.isArray(items) && items.includes(productKey);
  } catch {
    return false;
  }
}

class VoyadoAutocomplete extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._config = null;
    this._debounceTimer = null;
    this._form = null;
    this._input = null;
    this._resultsContainer = null;
    this._autocompleteConfig = null;
    this._liveRegion = null;
    this._debounceMs = 200;
    this._handleInput = this._handleInput.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleFocus = this._handleFocus.bind(this);
    this._navItems = [];
    this._navIndex = -1;
    this._handleKeydown = this._handleKeydown.bind(this);
    this._openObserver = null;
  }

  connectedCallback() {
    const formId = this.dataset.form;
    const resultsId = this.dataset.results;

    if (!formId || !resultsId) {
      console.warn('[voyado-autocomplete] Missing data-form or data-results attribute.');
      return;
    }

    this._form = document.getElementById(formId);
    const resultsHost = document.getElementById(resultsId);

    if (!this._form || !resultsHost) {
      console.warn('[voyado-autocomplete] Could not find form or results element.');
      return;
    }

    this._input = this._form.elements['q'];
    this._resultsContainer = resultsHost.querySelector('[slot="results"]');

    if (!this._input || !this._resultsContainer) {
      console.warn('[voyado-autocomplete] Could not find input[name="q"] or [slot="results"].');
      return;
    }

    // Input ARIA
    this._input.setAttribute('role', 'combobox');
    this._input.setAttribute('aria-autocomplete', 'list');
    this._input.setAttribute('aria-expanded', 'false');
    this._input.setAttribute('aria-haspopup', 'listbox');
    this._input.setAttribute('autocomplete', 'off');

    // Results container
    this._resultsContainer.setAttribute('role', 'listbox');
    this._resultsContainer.id = 'voyado-ac-listbox';
    this._input.setAttribute('aria-controls', 'voyado-ac-listbox');

    try {
      this._config = readConfig();
    } catch (error) {
      console.warn(error instanceof Error ? error.message : '[voyado-autocomplete] Voyado config unavailable.');
      return;
    }

    this._autocompleteConfig = readAutocompleteConfig();
    this._debounceMs = this._autocompleteConfig.api.debounceMs || 200;
    this._ensureLiveRegion();

    // Capture phase so we intercept before Prestige's own input listener (bubble phase)
    this._input.addEventListener('input', this._handleInput, { capture: true });
    // Zero-state: show recent searches + recently viewed when input is focused while empty
    this._input.addEventListener('focus', this._handleFocus);
    this._input.addEventListener('keydown', this._handleKeydown);

    // Delegated click handler for ticket notifications — registered once
    this._resultsContainer.addEventListener('click', this._handleClick);

    // Trigger zero-state fetch when the search dialog opens (open attribute added to header-search),
    // so results appear immediately without requiring a click inside the input.
    const headerSearch = this.closest('header-search');
    if (headerSearch) {
      this._openObserver = new MutationObserver(() => {
        if (headerSearch.hasAttribute('open') && this._input.value.trim() === '') {
          this._fetch('');
        }
      });
      this._openObserver.observe(headerSearch, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    if (this._input) {
      this._input.removeEventListener('input', this._handleInput, { capture: true });
      this._input.removeEventListener('focus', this._handleFocus);
      this._input.removeEventListener('keydown', this._handleKeydown);
    }
    if (this._resultsContainer) {
      this._resultsContainer.removeEventListener('click', this._handleClick);
    }
    if (this._openObserver) {
      this._openObserver.disconnect();
      this._openObserver = null;
    }
    clearTimeout(this._debounceTimer);
    this._abort();
  }

  _handleFocus() {
    if (this._input.value.trim() === '') {
      this._fetch('');
    }
  }

  _handleInput(event) {
    const phrase = this._input.value.trim();

    // Always prevent Prestige's native predictive-search listener — we own this input
    event.stopImmediatePropagation();

    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._fetch(phrase), this._debounceMs);
  }

  _handleClick(event) {
    const removePhraseButton = event.target.closest('[data-voyado-remove-phrase]');
    if (removePhraseButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const phrase = removePhraseButton.dataset.voyadoRemovePhrase || '';
      if (!phrase || !this._config) return;

      sendRemoveRecentSearchesNotification(this._config.cluster, this._config.market, phrase);

      const item = removePhraseButton.closest('li');
      const panel = removePhraseButton.closest('.voyado-autocomplete__recent-searches');
      const list = removePhraseButton.closest('.voyado-autocomplete__recent-search-list');
      item?.remove();
      if (list && !list.querySelector('li')) {
        panel?.remove();
      }

      return;
    }

    const removeProductButton = event.target.closest('[data-voyado-remove-product-key]');
    if (removeProductButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const productKey = removeProductButton.dataset.voyadoRemoveProductKey || '';
      if (!productKey || !this._config) return;

      sendRemoveRecentlyViewedNotification(this._config.cluster, this._config.market, productKey);

      const card = removeProductButton.closest('.voyado-autocomplete__product');
      const panel = removeProductButton.closest('.voyado-autocomplete__recently-viewed');
      const list = removeProductButton.closest('.voyado-autocomplete__product-list');
      card?.remove();
      if (list && !list.querySelector('.voyado-autocomplete__product')) {
        panel?.remove();
      }

      return;
    }

    const link = event.target.closest('a[data-voyado-ticket]');
    if (!link) return;
    const ticket = link.dataset.voyadoTicket;
    if (!ticket || !this._config) return;
    const { cluster, market } = this._config;
    sendClickNotification(cluster, market, ticket);
  }

  _abort() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  _ensureLiveRegion() {
    if (!this._liveRegion) {
      this._liveRegion = document.createElement('span');
      this._liveRegion.className = 'sr-only voyado-autocomplete__live';
      this._liveRegion.setAttribute('aria-live', 'polite');
    }

    return this._liveRegion;
  }

  _setResultsMarkup(html, liveMessage = '') {
    if (!this._resultsContainer) return;

    this._resultsContainer.innerHTML = html;
    const liveRegion = this._ensureLiveRegion();
    this._resultsContainer.appendChild(liveRegion);
    liveRegion.textContent = liveMessage;
  }

  _renderLoading() {
    this._navItems = [];
    this._navIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    this._input.removeAttribute('aria-activedescendant');
    this._setResultsMarkup(
      '<div class="voyado-autocomplete__loading" aria-live="polite" aria-label="Suche lädt..."><span class="voyado-autocomplete__loading-dots"></span></div>',
      'Suche lädt...'
    );
  }

  async _fetch(phrase) {
    // Abort and create the controller synchronously before any await so that
    // concurrent calls (MutationObserver + focus both firing on search open)
    // invalidate each other's signals before fetch() is ever called.
    // A pre-aborted signal causes fetch() to throw AbortError immediately,
    // avoiding any partial-body read that would produce a SyntaxError.
    this._abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    await _syncOnce;
    await waitForConsent();

    if (!this._config) {
      return;
    }

    const { cluster, market, locale } = this._config;

    if (!this._autocompleteConfig) {
      this._autocompleteConfig = readAutocompleteConfig();
    }

    // Read section-level params and ordered panel list
    const { api: ac, zeroPanels, activePanels } = this._autocompleteConfig;
    const panels = phrase ? activePanels : zeroPanels;

    // q is omitted from urlParams so buildUrl does not skip it when empty.
    // We set it explicitly below to ensure q='' is sent for the zero-state request,
    // which is what triggers Voyado to return recentSearches and recentlyViewed.
    const urlParams = {
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      touchpoint: getTouchpoint(),
      market,
      locale,
    };

    // Add optional URL params only when non-empty
    if (ac.channels) urlParams.channels = ac.channels;
    if (ac.priceId) urlParams.priceId = ac.priceId;
    if (ac.stores) urlParams.stores = ac.stores;
    if (ac.templateId) urlParams.templateId = ac.templateId;
    if (ac.viewId && ac.viewId !== 'production') urlParams.viewId = ac.viewId.toUpperCase();
    if (new URLSearchParams(location.search).get('PREVIEW') === 'true') urlParams.viewId = 'preview';
    if (ac.presentCustom) urlParams.presentCustom = ac.presentCustom;
    if (ac.presentPrices) urlParams.presentPrices = ac.presentPrices;
    // notify=false in the Voyado API means "do not register behavioral data"
    if (!hasVoyadoConsent() || isBotOrCrawler()) urlParams.notify = 'false';

    const url = buildUrl(cluster, '/api/storefront/v3/queries/autocomplete', urlParams);
    // Voyado returns recentSearches/recentlyViewed only when q is absent entirely.
    // With any q value (including q='') it treats the request as an active search
    // and returns empty arrays for those fields.
    if (phrase) {
      url.searchParams.set('q', phrase);
    }

    // Build POST body — include contentLists for each content panel with max > 0
    const body = {};
    const contentPanels = panels.filter(b => b.type === 'content');
    if (contentPanels.length > 0) {
      body.contentLists = contentPanels.map(b => ({ id: b.listId }));
    }
    if (ac.productRules) {
      body.productRules = ac.productRules;
    }

    this._renderLoading();

    try {
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: signal,
      });

      if (!response.ok) {
        this._clear();
        return;
      }

      const data = await response.json();
      this._render(data, phrase, panels);
    } catch (err) {
      if (err.name !== 'AbortError') {
        this._clear();
      }
    }
  }

  _clear() {
    this._navItems = [];
    this._navIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    this._input.removeAttribute('aria-activedescendant');
    this._setResultsMarkup('', '');
  }

  _render(data, phrase, panels) {
    const encodedPhrase = encodeURIComponent(phrase);
    const searchUrl = `/search?q=${encodedPhrase}&type=product`;
    const totalHits = data.totalHits ?? 0;
    const { labels } = this._autocompleteConfig || { labels: {} };
    const leftPanels = [];
    const rightPanels = [];

    for (const block of panels) {
      let panelHtml = '';

      switch (block.type) {
        case 'phrases':
          panelHtml = this._renderPhrases(data, phrase, block);
          break;
        case 'products':
          panelHtml = this._renderProducts(data, searchUrl, totalHits, block);
          break;
        case 'recent_searches':
          panelHtml = this._renderRecentSearches(data, block);
          break;
        case 'recently_viewed':
          panelHtml = this._renderRecentlyViewed(data, block);
          break;
        case 'content':
          panelHtml = this._renderContent(data, block);
          break;
        case 'redirect':
          this._handleRedirect(data);
          break;
        default:
          break;
      }

      if (!panelHtml) continue;

      if (block.type === 'products') {
        rightPanels.push(panelHtml);
      } else {
        leftPanels.push(panelHtml);
      }
    }

    if (leftPanels.length === 0 && rightPanels.length === 0) {
      const noResultsLabel = labels?.noResults || 'Keine Ergebnisse gefunden';
      this._setResultsMarkup(
        `<div class="voyado-autocomplete-results"><p class="voyado-autocomplete__empty">${escapeHtml(noResultsLabel)}</p></div>`,
        noResultsLabel
      );
      this._setNavItems();
      return;
    }

    const resultClasses = ['voyado-autocomplete-results'];
    if (leftPanels.length === 0) resultClasses.push('voyado-autocomplete-results--right-only');
    if (rightPanels.length === 0) resultClasses.push('voyado-autocomplete-results--left-only');

    let html = `<div class="${resultClasses.join(' ')}">`;
    if (leftPanels.length > 0) {
      html += `<div class="voyado-autocomplete__left">${leftPanels.join('')}</div>`;
    }
    if (rightPanels.length > 0) {
      html += `<div class="voyado-autocomplete__right">${rightPanels.join('')}</div>`;
    }
    html += '</div>';

    this._setResultsMarkup(html, totalHits > 0 ? `${totalHits} Ergebnisse gefunden` : (labels?.noResults || 'Keine Ergebnisse gefunden'));
    this._setNavItems();
  }

  /**
   * Renders phrase suggestions panel.
   * When phrase is empty (trending), uses block.labelEmpty if set.
   * @returns {string} HTML fragment or ''
   */
  _renderPhrases(data, phrase, block) {
    const phrases = Array.isArray(data.phraseSuggestions) ? data.phraseSuggestions : [];
    if (phrases.length === 0) return '';

    const label = phrase ? (block.label || 'Suchvorschläge') : (block.labelEmpty || block.label || 'Trendsuchen');
    let html = '<div class="voyado-autocomplete__phrases">';
    html += `<p class="voyado-autocomplete__category h6 text-subdued">${escapeHtml(label)}</p>`;
    html += `<ul class="voyado-autocomplete__phrase-list" aria-label="${escapeAttr(label)}">`;
    for (const p of phrases) {
      const suggestion = p.q ?? '';
      let displayHtml = sanitiseHighlight(p.highlighted || suggestion);
      // Guard against the API omitting spaces in the highlighted field (e.g. "{verona}geldbörse"
      // instead of "{verona} geldbörse") — fall back to plain text to avoid merged words.
      if (displayHtml.replace(/<\/?mark>/g, '') !== escapeHtml(suggestion)) {
        displayHtml = escapeHtml(suggestion);
      }
      const phraseUrl = `/search?q=${encodeURIComponent(suggestion)}&type=product&origin=PHRASE_SUGGEST`;
      html += `<li>`;
      html += `<a href="${escapeAttr(phraseUrl)}" class="voyado-autocomplete__phrase-link voyado-autocomplete__phrase-link--icon"><span>${displayHtml}</span></a>`;
      html += `</li>`;
    }
    html += '</ul></div>';
    return html;
  }

  /**
   * Renders product suggestions panel with total-hits link and view-all link.
   * @returns {string} HTML fragment or ''
   */
  _renderProducts(data, searchUrl, totalHits, block) {
    const productGroups = Array.isArray(data.productSuggestions) ? data.productSuggestions : [];
    const products = productGroups
      .flatMap(g => (Array.isArray(g.products) && g.products.length > 0 ? [g.products[0]] : []));

    if (products.length === 0) return '';

    const label = block.label || 'Produkte';
    let html = '<div class="voyado-autocomplete__products">';
    html += '<div class="voyado-autocomplete__products-header">';
    html += `<p class="voyado-autocomplete__category h6 text-subdued">${escapeHtml(label)}</p>`;
    if (totalHits > 0) {
      html += `<a href="${escapeAttr(searchUrl)}" class="voyado-autocomplete__total-hits smallcaps">${escapeHtml(String(totalHits))} Ergebnisse anzeigen</a>`;
    }
    html += '</div>';

    html += `<div class="voyado-autocomplete__product-list" aria-label="${escapeAttr(label)}">`;
    for (const product of products) {
      html += this._renderProductCard(product, block);
    }
    html += `</div>`;

    if (totalHits > 0) {
      const viewAllLabel = (block.viewAllLabel || 'Alle __count__ Produkte anzeigen').replace('__count__', String(totalHits));
      html += `<div class="voyado-autocomplete__view-all justify-self-center">`;
      html += `<a href="${escapeAttr(searchUrl)}" class="voyado-autocomplete__view-all-button">${escapeHtml(viewAllLabel)}</a>`;
      html += `</div>`;
    }
    html += `</div>`;
    return html;
  }

  /**
   * Renders recent searches panel (past search terms, no highlight).
   * @returns {string} HTML fragment or ''
   */
  _renderRecentSearches(data, block) {
    const searches = Array.isArray(data.recentSearches) ? data.recentSearches : [];
    if (searches.length === 0) return '';

    const label = block.label || 'Letzte Suchen';
    let html = '<div class="voyado-autocomplete__recent-searches">';
    html += `<p class="voyado-autocomplete__category h6 text-subdued">${escapeHtml(label)}</p>`;
    html += `<ul class="voyado-autocomplete__recent-search-list" aria-label="${escapeAttr(label)}">`;
    for (const s of searches) {
      const term = s.q ?? '';
      const phraseUrl = `/search?q=${encodeURIComponent(term)}&type=product&origin=RECENT_SEARCH`;
      html += `<li class="voyado-autocomplete__recent-search-item">`;
      html += `<a href="${escapeAttr(phraseUrl)}" class="voyado-autocomplete__phrase-link voyado-autocomplete__phrase-link--icon">${escapeHtml(term)}</a>`;
      if (block.showRemove) {
        html += `<button class="voyado-autocomplete__remove-btn" aria-label="Suche entfernen" data-voyado-remove-phrase="${escapeAttr(term)}" type="button">×</button>`;
      }
      html += `</li>`;
    }
    html += '</ul></div>';
    return html;
  }

  /**
   * Renders recently viewed products panel (same ProductGroup[] shape as productSuggestions).
   * No total-hits or view-all link.
   * @returns {string} HTML fragment or ''
   */
  _renderRecentlyViewed(data, block) {
    const groups = Array.isArray(data.recentlyViewed) ? data.recentlyViewed : [];
    const products = groups
      .flatMap(g => (Array.isArray(g.products) && g.products.length > 0 ? [g.products[0]] : []));

    if (products.length === 0) return '';

    const label = block.label || 'Zuletzt angesehen';
    let html = '<div class="voyado-autocomplete__recently-viewed">';
    html += `<p class="voyado-autocomplete__category h6 text-subdued">${escapeHtml(label)}</p>`;
    html += `<div class="voyado-autocomplete__product-list" aria-label="${escapeAttr(label)}">`;
    for (const product of products) {
      html += this._renderProductCard(product, block, {
        allowRemove: block.showRemove,
        removeProductKey: getProductKey(product),
      });
    }
    html += `</div></div>`;
    return html;
  }

  /**
   * Renders a content list panel, matching block.listId against returned contentLists.
   * @returns {string} HTML fragment or ''
   */
  _renderContent(data, block) {
    const allLists = Array.isArray(data.contentLists) ? data.contentLists : [];
    const list = allLists.find(l => l.id === block.listId);
    if (!list) return '';
    const items = Array.isArray(list.items) ? list.items : [];
    if (items.length === 0) return '';

    const label = block.label || 'Inhalte';
    let html = '<div class="voyado-autocomplete__content">';
    html += `<p class="voyado-autocomplete__category h6 text-subdued">${escapeHtml(label)}</p>`;
    html += `<ul class="voyado-autocomplete__content-list" aria-label="${escapeAttr(label)}">`;
    for (const item of items) {
      const title = item.title ?? item.name ?? '';
      const link = safeUrl(item.link ?? item.url ?? '#');
      const ticket = item.ticket ?? '';
      const imageUrl = safeUrl(item.imageUrl ?? '');
      const imageAlt = item.imageAlt ?? title;
      const description = item.description ?? '';
      const hasImage = block.showImage && imageUrl && imageUrl !== '#';
      html += `<li>`;
      html += `<a href="${escapeAttr(link)}" class="voyado-autocomplete__content-link${hasImage ? ' voyado-autocomplete__content-link--with-image' : ''}" data-voyado-ticket="${escapeAttr(ticket)}">`;
      if (hasImage) {
        html += `<span class="voyado-autocomplete__content-image">`;
        html += `<img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(imageAlt)}" loading="lazy" width="40" height="40">`;
        html += `</span>`;
      }
      html += `<span class="voyado-autocomplete__content-body">`;
      html += `<span class="voyado-autocomplete__content-title">${escapeHtml(title)}</span>`;
      if (block.showDescription && description) {
        html += `<span class="voyado-autocomplete__content-description text-subdued">${escapeHtml(description)}</span>`;
      }
      html += `</span>`;
      html += `</a>`;
      html += `</li>`;
    }
    html += '</ul></div>';
    return html;
  }

  _getMinPriceValue(price) {
    if (typeof price === 'number') return price;
    if (price && typeof price === 'object' && typeof price.min === 'number') {
      return price.min;
    }
    return null;
  }

  _isSoldOut(product) {
    return product?.inStock === false ||
      product?.isInStock === false ||
      product?.availability === 'OUT_OF_STOCK' ||
      product?.stockStatus === 'OUT_OF_STOCK';
  }

  _renderProductCard(product, block, options = {}) {
    const { labels } = this._autocompleteConfig || { labels: {} };
    const title = String(product?.custom?.br_0003_title?.[0]?.label || product?.title || '');
    const subtitle = String(product?.custom?.br_0003_subtitle?.[0]?.label || '');
    const link = safeUrl(product.link ?? '#');
    const ticket = product.ticket ?? '';
    const productKey = getProductKey(product);
    const favoritesEnabled = this._config?.favoritesEnabled === true;
    const isFav = favoritesEnabled && productKey ? isFavoritedProductKey(productKey) : false;
    const imageUrl = safeUrl(
      product.imageInfo?.thumbnail ??
      product.imageInfo?.images?.[0]?.sources?.[0]?.url ??
      ''
    );
    const imageAlt = product.imageInfo?.images?.[0]?.alt ?? title;
    const sellingRaw = product?.sellingPrice ?? product?.price ?? product?.prices?.[0]?.sellingPrice ?? product?.prices?.[0]?.amount ?? product?.prices?.[0] ?? null;
    const compareAtRaw = product?.listPrice ?? product?.originalPrice ?? product?.prices?.[0]?.listPrice ?? null;
    const sellingPrice = formatPrice(sellingRaw);
    const compareAtPrice = formatPrice(compareAtRaw);
    const sellingMin = this._getMinPriceValue(sellingRaw);
    const compareAtMin = this._getMinPriceValue(compareAtRaw);
    const isOnSale = sellingMin != null && compareAtMin != null && sellingMin < compareAtMin;
    const soldOut = this._isSoldOut(product);

    let html = `<article class="voyado-autocomplete__product" aria-label="${escapeAttr(title)}">`;
    if (favoritesEnabled && productKey) {
      html += `
        <button
          class="voyado-favorite-btn${isFav ? ' is-favorited' : ''}"
          data-voyado-favorite-btn
          data-voyado-product-key="${escapeAttr(productKey)}"
          aria-pressed="${isFav ? 'true' : 'false'}"
          aria-label="Zu Favoriten hinzufügen"
          type="button"
        ></button>
      `;
    }
    if (options.allowRemove && options.removeProductKey) {
      html += `<button class="voyado-autocomplete__remove-btn voyado-autocomplete__remove-btn--overlay" aria-label="Produkt aus Verlauf entfernen" data-voyado-remove-product-key="${escapeAttr(options.removeProductKey)}" type="button">×</button>`;
    }
    html += `<a href="${escapeAttr(link)}" class="voyado-autocomplete__product-link" data-voyado-ticket="${escapeAttr(ticket)}">`;
    if (block.showImage) {
      html += `<span class="voyado-autocomplete__product-image">`;
      if (imageUrl && imageUrl !== '#') {
        html += `<img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(imageAlt)}" loading="lazy" width="100" height="100">`;
      }
      html += `</span>`;
    }
    html += `<span class="voyado-autocomplete__product-info">`;
    html += `<span class="voyado-autocomplete__product-title">${escapeHtml(title)}</span>`;
    if (subtitle) {
      html += `<span class="voyado-autocomplete__product-subtitle text-subdued">${escapeHtml(subtitle)}</span>`;
    }
    if (block.showPrice && sellingPrice) {
      html += `<span class="voyado-autocomplete__product-price-wrap">`;
      if (isOnSale && compareAtPrice) {
        html += `<span class="voyado-autocomplete__product-price voyado-autocomplete__product-price--sale text-on-sale">${escapeHtml(sellingPrice)}</span>`;
        html += `<span class="voyado-autocomplete__product-price voyado-autocomplete__product-price--compare text-subdued">${escapeHtml(compareAtPrice)}</span>`;
      } else {
        html += `<span class="voyado-autocomplete__product-price">${escapeHtml(sellingPrice)}</span>`;
      }
      html += `</span>`;
    }
    if (soldOut) {
      html += `<span class="voyado-autocomplete__product-badge smallcaps">${escapeHtml(labels?.soldOut || 'Ausverkauft')}</span>`;
    }
    html += `</span>`;
    html += `</a>`;
    html += `</article>`;
    return html;
  }

  /**
   * Handles redirect on exact match. Side-effect only — returns no HTML.
   * Validates the URL with safeUrl() before navigating.
   */
  _handleRedirect(data) {
    const link = data.redirectLink;
    if (typeof link !== 'string' || link === '') return;
    const safe = safeUrl(link);
    if (safe && safe !== '#') {
      window.location.assign(safe);
    }
  }

  _setNavItems() {
    const items = Array.from(
      this._resultsContainer.querySelectorAll(
        'a.voyado-autocomplete__phrase-link, a.voyado-autocomplete__content-link, a.voyado-autocomplete__product-link'
      )
    );
    items.forEach((item, i) => {
      item.id = `vac-opt-${i}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
    });
    this._navItems = items;
    this._navIndex = -1;
    this._input.setAttribute('aria-expanded', items.length > 0 ? 'true' : 'false');
    this._input.removeAttribute('aria-activedescendant');
  }

  _setNavIndex(index) {
    const count = this._navItems.length;
    if (count === 0) return;
    const next = Math.max(0, Math.min(index, count - 1));

    if (this._navIndex >= 0 && this._navItems[this._navIndex]) {
      this._navItems[this._navIndex].setAttribute('aria-selected', 'false');
      this._navItems[this._navIndex].classList.remove('is-selected');
    }

    this._navIndex = next;
    const item = this._navItems[next];
    if (item) {
      item.setAttribute('aria-selected', 'true');
      item.classList.add('is-selected');
      this._input.setAttribute('aria-activedescendant', item.id);
      item.scrollIntoView({ block: 'nearest' });
    }
  }

  _handleKeydown(event) {
    if (!this._navItems.length) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._setNavIndex(this._navIndex < 0 ? 0 : this._navIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this._navIndex <= 0) {
          // Return to no-selection state
          if (this._navIndex === 0 && this._navItems[0]) {
            this._navItems[0].setAttribute('aria-selected', 'false');
            this._navItems[0].classList.remove('is-selected');
          }
          this._navIndex = -1;
          this._input.removeAttribute('aria-activedescendant');
        } else {
          this._setNavIndex(this._navIndex - 1);
        }
        break;
      case 'Enter':
        if (this._navIndex >= 0 && this._navItems[this._navIndex]) {
          event.preventDefault();
          this._navItems[this._navIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this._clear();
        break;
    }
  }
}

if (!window.customElements.get('voyado-autocomplete')) {
  window.customElements.define('voyado-autocomplete', VoyadoAutocomplete);
}
